import React, { useState } from "react";
import { Check } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "../ui/command";
import { QUOTE_LANGUAGES } from "../../constants/languages";

interface LanguagePickerProps {
  // Checked language code.
  value: string;
  onSelect: (languageCode: string) => void;
  // Marked "(original)" - the language the quote was written in.
  originalLanguage?: string;
  align?: "start" | "center" | "end";
  // The button that opens the picker (rendered via asChild).
  children: React.ReactElement;
  // Wraps the trigger, e.g. in a tooltip - the trigger must stay the
  // tooltip's direct child for both to attach to the same button.
  wrapTrigger?: (trigger: React.ReactElement) => React.ReactNode;
}

// Searchable list of every Google Translate language - with ~250 entries a
// plain dropdown is unusable, so this mirrors Google Translate's "Search
// languages" box. Matches on the language name or its code ("ta", "zh-CN").
export function LanguagePicker({ value, onSelect, originalLanguage, align = "start", children, wrapTrigger }: LanguagePickerProps) {
  const [open, setOpen] = useState(false);
  const trigger = <PopoverTrigger asChild>{children}</PopoverTrigger>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {wrapTrigger ? wrapTrigger(trigger) : trigger}
      <PopoverContent align={align} className="w-64 p-0">
        <Command>
          <CommandInput placeholder="Search languages" />
          <CommandList className="max-h-72 themed-scrollbar">
            <CommandEmpty>No language found.</CommandEmpty>
            {QUOTE_LANGUAGES.map((lang) => (
              <CommandItem
                key={lang.code}
                value={`${lang.label} ${lang.code}`}
                onSelect={() => {
                  onSelect(lang.code);
                  setOpen(false);
                }}
                className="flex items-center justify-between gap-2"
              >
                <span>
                  {lang.label}
                  {lang.code === originalLanguage && <span className="ml-1 text-xs text-slate-400">(original)</span>}
                </span>
                {value === lang.code && <Check className="w-3.5 h-3.5 shrink-0" />}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
